/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  TSmartCarouselCarouselGetParams,
  TSmartCarouselScreenGetParams,
  TSmartCarouselLiveTvGetParams
} from "@tivocorp/data-domain-lib";
import { FeedItemType } from "client-code/smart-search";

import { mapCarouselResponseToTilePage } from "../homescreen/homescreenMappers";
import type { TTilePage } from "./carouselPagination";
import { OfferType } from "../../model/enums";
import { LocationUrls } from "../../model/navigation";
import { PostSigninInteractor } from "../../model/interactor";
import { ChannelsInteractor } from "../../model/interactor/channelsInteractor";
import { Log } from "../../model/logging";
import {
  NdvrPlaybackType,
  PlaybackContentType,
  IdType
} from "../../model/videoplayer/enums";
import type { IPlayable } from "../../model/videoplayer/interfaces";
import { VideoPlaybackController } from "../../model/videoplayer/VideoPlaybackController";
import type { TCarouselItem } from "../../model/types";

// ─── Image Host Cache ───

const IMAGE_HOST_SERVICE_NAME: string = "image-host";

let cachedImageHostBaseUrl: string | null = null;

/**
 * Lazily cached image-host base URL from TSRI.
 * Shared across all carousel feature slices.
 * Persists for the lifetime of the app session.
 */
export const getImageHostBaseUrl = async (
  interactor: PostSigninInteractor
): Promise<string> => {
  if (cachedImageHostBaseUrl === null) {
    cachedImageHostBaseUrl = await interactor.getServiceEndpointByServiceName(
      IMAGE_HOST_SERVICE_NAME
    );
  }
  return cachedImageHostBaseUrl;
};

/**
 * Clears the shared image-host URL cache.
 * Call this during sign-out or when TSRI endpoints may have changed.
 */
export const clearSmartCarouselApiCache = (): void => {
  cachedImageHostBaseUrl = null;
};

// ─── Param Builders ───

/**
 * Builds a SmartCarousel screen GET params object.
 * Callers supply screen-specific values (name, page sizes, cursor).
 */
export const buildScreenGetParams = async (
  screenName: string,
  count: number,
  initialFeedItemCount: number,
  pageCursor: string | null
): Promise<TSmartCarouselScreenGetParams> => {
  const params: TSmartCarouselScreenGetParams = {
    screenName,
    count,
    initialFeedItemCount
  };
  if (pageCursor !== null && pageCursor.length > 0) {
    params.pageCursor = pageCursor;
  }
  return params;
};

/**
 * Builds a SmartCarousel carousel GET params object for offset-based tile fetching.
 */
export const buildCarouselGetParams = async (
  carouselName: string,
  offset: number,
  count: number
): Promise<TSmartCarouselCarouselGetParams> => ({
  carouselName,
  offset,
  count
});

/**
 * Builds a SmartCarousel liveTv GET params object for initial Live TV data.
 * Callers supply offset and count for pagination.
 */
export const buildLiveTvGetParams = async (
  offset: number,
  count: number
): Promise<TSmartCarouselLiveTvGetParams> => ({
  offset,
  count
});

// ─── Tile Playback ───

const TAG: string = "smartCarouselUtility";

/**
 * Resolves the channel a linear tile plays on.
 *
 * Live and SOCU tiles are both channel-bound and both identify the channel by
 * stationId — LinearProvider tiles carry it as the tile id instead. Returns
 * null when the tile names no station or the lineup holds no channel for it;
 * callers treat that as a no-op.
 */
const resolveTileChannel = async (feedItem: TCarouselItem, tag: string) => {
  let stationId: string | undefined = feedItem.stationId;

  if (stationId === undefined || stationId === "") {
    if (feedItem.feedItemType === FeedItemType.LinearProvider) {
      stationId = feedItem.id;
    } else {
      Log.w(TAG, tag + " - No stationId");
      return null;
    }
  }

  const interactor: ChannelsInteractor = new ChannelsInteractor();
  const channel = await interactor.getChannelByStationId(stationId);

  if (channel === null) {
    Log.w(TAG, tag + " - Channel not found for stationId: " + stationId);
  }

  return channel;
};

/**
 * Shared tile playback logic used by both homescreen and player carousel.
 * Returns true if live playback was started, false for no-op cases.
 * Throws on unexpected errors (caller should rejectWithValue).
 */
export const handleTileSelectionPlayback = async (
  feedItem: TCarouselItem,
  tag: string
): Promise<boolean> => {
  const isFutureContent: boolean =
    feedItem.startTime !== undefined &&
    feedItem.startTime !== null &&
    Date.parse(feedItem.startTime) > Date.now();
  if (isFutureContent) {
    Log.i(TAG, tag + " - Tile is for future content, skipping");
    return false;
  }

  const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();

  if (feedItem.offerType === OfferType.Live) {
    const channel = await resolveTileChannel(feedItem, tag);

    if (channel === null) {
      return false;
    }

    Log.i(TAG, tag + " - Starting live playback: " + channel.stationId);
    const livePlayable: IPlayable = {
      channel,
      playbackContentType: PlaybackContentType.LIVE
    };
    vpc.startPlayback(livePlayable);
    return true;
  }

  // SOCU plays the same way VOD does, but the stream is channel-bound, so the
  // playable carries the channel object as well — resolved from the tile's
  // stationId the same way the LIVE branch resolves it.
  if (feedItem.offerType === OfferType.Socu) {
    const channel = await resolveTileChannel(feedItem, tag);

    if (channel === null) {
      return false;
    }

    Log.i(TAG, tag + " - Starting SOCU playback: " + feedItem.offerId);
    const socuPlayable: IPlayable = {
      offerId: feedItem.offerId,
      channel,
      playbackContentType: PlaybackContentType.STARTOVER_RESUME,
      vodPlaybackModel: { id: feedItem.offerId || "", isStartOver: false }
    };
    vpc.startPlayback(socuPlayable);
    return true;
  }

  if (feedItem.offerType === OfferType.Vod) {
    Log.i(TAG, tag + " - Starting VOD playback: " + feedItem.id);
    // The watch action is resolved from the content endpoint by the tile's
    // feed item id, which is its content id.
    const vodPlayable: IPlayable = {
      playbackContentType: PlaybackContentType.VOD,
      vodPlaybackModel: {
        id: feedItem.id || "",
        idType: IdType.CONTENT,
        isStartOver: false
      }
    };
    vpc.startPlayback(vodPlayable);
    return true;
  }

  if (feedItem.offerType === OfferType.Recording) {
    Log.i(TAG, tag + " - Starting NDVR playback: " + feedItem.id);
    const ndvrPlayable: IPlayable = {
      playbackContentType: PlaybackContentType.NDVR_RECORDED,
      ndvrPlaybackModel: {
        id: feedItem.id,
        ndvrType: NdvrPlaybackType.RECORDING,
        isStartOver: true
      }
    };
    vpc.startPlayback(ndvrPlayable);
    return true;
  }

  Log.i(
    TAG,
    tag + " - offerType=" + String(feedItem.offerType) + " not supported"
  );
  return false;
};

// ─── Tile Selection ───

/** Router navigation, as `useNavigationService().navigateTo` provides it. */
export type TTileNavigateTo = (
  url: string,
  replace?: boolean,
  state?: Record<string, unknown>
) => void;

/** Where selecting a tile leads. */
export enum TileSelectionDestination {
  CONTENT_DETAIL = "contentDetail",
  PLAYER = "player"
}

/** Offer types that play straight from a non-series tile. */
const DIRECT_PLAY_OFFER_TYPES: ReadonlySet<OfferType> = new Set<OfferType>([
  OfferType.Live,
  OfferType.Recording,
  OfferType.Socu,
  OfferType.Vod
]);

/**
 * Decides where a tile goes when selected.
 *
 * A series always opens Content Detail, whatever its offer: there is no single
 * episode to play. Any other tile plays when it carries exactly one playable
 * offer (Live, Recording, Socu, Vod) and opens Content Detail otherwise —
 * `Any` means several offers, which the detail screen lets the user pick from,
 * and a tile with no offer type has nothing to play.
 */
export const resolveTileSelectionDestination = (
  feedItemType: string | undefined,
  offerType: OfferType | undefined
): TileSelectionDestination => {
  if (feedItemType === FeedItemType.Series) {
    return TileSelectionDestination.CONTENT_DETAIL;
  }
  if (offerType !== undefined && DIRECT_PLAY_OFFER_TYPES.has(offerType)) {
    return TileSelectionDestination.PLAYER;
  }
  return TileSelectionDestination.CONTENT_DETAIL;
};

/**
 * Opens Content Detail for an item. A series is seeded by its unified
 * collection id, anything else by its content id.
 */
export const openContentDetail = (
  navigateTo: TTileNavigateTo,
  feedItemType: string | undefined,
  itemId: string
): void => {
  navigateTo(LocationUrls.CONTENT_DETAIL, undefined, {
    seedType:
      feedItemType === FeedItemType.Series
        ? "unifiedCollectionId"
        : "contentId",
    seedId: itemId
  });
};

/**
 * Selects a carousel tile: opens Content Detail or starts playback and opens
 * the player, per {@link resolveTileSelectionDestination}.
 *
 * A live movie without a station opens Content Detail to resolve its offers.
 *
 * Resolves to the destination navigated to, or null when playback did not
 * start (future airing, unresolvable channel) and so nothing was opened.
 * Throws on unexpected playback errors, as {@link handleTileSelectionPlayback}
 * does.
 */
export const handleTileSelection = async (
  feedItem: TCarouselItem,
  navigateTo: TTileNavigateTo,
  tag: string
): Promise<TileSelectionDestination | null> => {
  const isLiveMovieWithoutStation: boolean =
    feedItem.feedItemType === FeedItemType.Movie &&
    feedItem.offerType === OfferType.Live &&
    !feedItem.stationId;
  const destination: TileSelectionDestination = isLiveMovieWithoutStation
    ? TileSelectionDestination.CONTENT_DETAIL
    : resolveTileSelectionDestination(
        feedItem.feedItemType,
        feedItem.offerType
      );

  if (destination === TileSelectionDestination.CONTENT_DETAIL) {
    Log.i(TAG, tag + " - Opening Content Detail: " + feedItem.id);
    openContentDetail(navigateTo, feedItem.feedItemType, feedItem.id);
    return destination;
  }

  const didStartPlayback: boolean = await handleTileSelectionPlayback(
    feedItem,
    tag
  );
  if (!didStartPlayback) {
    return null;
  }

  navigateTo(LocationUrls.PLAYER);
  return destination;
};

// ─── Tile Page Fetcher ───

/** Fetches the next tile page for a carousel rail using offset-count pagination. */
export const fetchCarouselTilePage = async (
  carouselId: string,
  offset: number,
  count: number
): Promise<TTilePage> => {
  const interactor: PostSigninInteractor = new PostSigninInteractor();
  const imageHostBaseUrl: string = await getImageHostBaseUrl(interactor);
  const params: TSmartCarouselCarouselGetParams = await buildCarouselGetParams(
    carouselId,
    offset,
    count
  );
  const response = await interactor.fetchCarousel(params);
  return mapCarouselResponseToTilePage(response, imageHostBaseUrl);
};
